import { Box, useTheme } from "@mui/material";
import React, { useState } from "react";

export interface TrendPoint {
  /** What the point is, for the hover readout: "Week of Oct 13". */
  label: string;
  value: number | null;
}

/**
 * The reference palette's de-emphasis ink for the weeks before, and categorical
 * slot 1 for the latest, in each mode.
 */
const INK = {
  light: { line: "#898781", latest: "#2a78d6" },
  dark: { line: "#898781", latest: "#3987e5" },
};

const PAD = 5;

/**
 * Twelve weeks as a small line, the latest week marked. The y-axis runs from
 * the lowest week to the highest, so it shows the shape, not the size; the
 * numbers are in the readout on hover and in the row's weekly table.
 */
export const TrendLine: React.FC<{
  points: TrendPoint[];
  format: (value: number | null) => string;
  width?: number;
  height?: number;
  /** What it shows, for screen readers: "Came back the next day, last 12 weeks". */
  title: string;
}> = ({ points, format, width = 132, height = 36, title }) => {
  const theme = useTheme();
  const ink = INK[theme.palette.mode === "dark" ? "dark" : "light"];
  // The ring around a marker is the card's own colour, so the line reads as passing behind it.
  const surface = theme.palette.background.paper;
  const [hovered, setHovered] = useState<number | null>(null);

  const values = points.map((point) => point.value).filter((value): value is number => value !== null);
  if (values.length === 0) {
    return (
      <Box sx={{ width, height, display: "flex", alignItems: "center", color: "text.secondary", fontSize: 12 }}>
        Not enough data yet
      </Box>
    );
  }
  const low = Math.min(...values);
  const high = Math.max(...values);
  const step = points.length > 1 ? (width - 2 * PAD) / (points.length - 1) : 0;
  const x = (index: number) => PAD + index * step;
  const y = (value: number) => (high === low ? height / 2 : PAD + (1 - (value - low) / (high - low)) * (height - 2 * PAD));

  // Break the line where a week has nothing to count rather than drawing through it.
  let path = "";
  points.forEach((point, index) => {
    if (point.value === null) return;
    const joined = index > 0 && points[index - 1].value !== null;
    path += `${joined ? "L" : "M"}${x(index).toFixed(1)},${y(point.value).toFixed(1)}`;
  });
  const lastIndex = points.length - 1;
  const last = points[lastIndex];
  const shown = hovered === null ? null : points[hovered];
  const first = values[0];
  const latest = values[values.length - 1];

  return (
    <Box sx={{ position: "relative", width, height }} onMouseLeave={() => setHovered(null)}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={`${title}: from ${format(first)} to ${format(latest)}.`}
        style={{ display: "block", overflow: "visible" }}
      >
        <path d={path} fill="none" stroke={ink.line} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {/* A week with no neighbour to join is still a week: mark it. */}
        {points.map((point, index) =>
          point.value !== null &&
          index !== lastIndex &&
          points[index - 1]?.value == null &&
          points[index + 1]?.value == null ? (
            <circle key={`dot-${point.label}`} cx={x(index)} cy={y(point.value)} r={2} fill={ink.line} />
          ) : null,
        )}
        {hovered !== null && points[hovered].value !== null && (
          <circle cx={x(hovered)} cy={y(points[hovered].value as number)} r={3.5} fill={ink.line} stroke={surface} strokeWidth={2} />
        )}
        {last.value !== null && (
          <circle cx={x(lastIndex)} cy={y(last.value)} r={4} fill={ink.latest} stroke={surface} strokeWidth={2} />
        )}
        {points.map((point, index) => (
          // Each week's hit area is its whole column, not the 2px line.
          <rect
            key={point.label}
            x={x(index) - Math.max(step, 12) / 2}
            y={0}
            width={Math.max(step, 12)}
            height={height}
            fill="transparent"
            onMouseEnter={() => setHovered(index)}
          />
        ))}
      </svg>
      {shown && (
        <Box
          sx={{
            position: "absolute",
            bottom: height + 4,
            left: Math.min(Math.max(x(hovered as number), 60), width - 20),
            transform: "translateX(-50%)",
            pointerEvents: "none",
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            boxShadow: 2,
            px: 1,
            py: 0.5,
            fontSize: 12,
            whiteSpace: "nowrap",
            zIndex: 2,
          }}
        >
          <Box component="span" sx={{ fontWeight: 600 }}>
            {format(shown.value)}
          </Box>{" "}
          <Box component="span" sx={{ color: "text.secondary" }}>
            {shown.label}
          </Box>
        </Box>
      )}
    </Box>
  );
};
